import React, { useState, useEffect } from "react";
import { useTaggingStore } from "./chatStore";
import topUser from "../../../../assets/images/topUser.png";
import X from "../../../../assets/blackX.svg";
import AvatarGroup from "./AvatarGroup";
import constant from "../../../../constant";
import { useParams } from "react-router-dom";
import usePutApi from "../../../../utils/hooks/usePutApi";
import { reportEndpoints } from "../../../../api/endpoints/report.endpoints";
import { useAuth } from "../../../context/AuthContext";

const UserTagging = ({ code, kpiName, alreadyTagged = [], onAssignmentChange }) => {
  const [inputValue, setInputValue] = useState("");
  const [triggeredAt, setTriggeredAt] = useState(false);
  const [showUserList, setShowUserList] = useState(false);
  const params = useParams();

  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const { data: assignRes, put: assignUserApi } = usePutApi(
    reportEndpoints.assignUser(params.id)
  );

  // Zustand store actions and state
  const {
    taggedUsers,
    suggestions,
    allUsers,
    fetchUsers,
    addUser,
    removeUser,
    setSuggestions,
    initializeTaggedUsers,
  } = useTaggingStore();

  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    setLoaded(false);
  }, [code]);

  useEffect(() => {
    if (allUsers?.length && !loaded) {
      const taggedIds = (alreadyTagged || []).map((id) => String(id));
      const mappedTaggedUsers = allUsers.filter((user) =>
        taggedIds.includes(String(user?.id))
      );

      initializeTaggedUsers(mappedTaggedUsers);
      setLoaded(true);
    }
  }, [alreadyTagged, allUsers, initializeTaggedUsers, loaded, code]);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputValue(value);

    if (showUserList) setShowUserList(false);

    const lastWord = value.split(" ").pop();
    if (lastWord.startsWith("@")) {
      const query = lastWord.slice(1).toLowerCase();

      const filteredUsers = allUsers?.filter(
        (user) =>
          user?.name?.toLowerCase()?.includes(query) &&
          !taggedUsers.some((tagged) => tagged.id === user.id)
      );
      setSuggestions(filteredUsers);
      setTriggeredAt(true);
    } else {
      setTriggeredAt(false);
      setSuggestions([]);
    }
  };

  const handleUserSelect = async (user) => {
    const currentIds = taggedUsers.map((u) => String(u.id));
    const updatedTaggedIds = [...new Set([...currentIds, String(user.id)])];

    try {
      await assignUserApi({
        assign: updatedTaggedIds.map((id) => ({ userId: id })),
        code,
        kpiName,
      });

      addUser(user);
      onAssignmentChange?.();
    } catch (err) {
      console.log("Error assigning user:", err);
    }

    const words = inputValue.split(" ");
    words.pop();
    setInputValue(words.join(" ") + " ");
    setSuggestions([]);
    setTriggeredAt(false);
    setInputValue("");
  };

  const handleDeleteCapsule = async (id) => {
    const updatedTaggedIds = taggedUsers
      .map((u) => String(u.id))
      .filter((taggedId) => taggedId !== String(id));

    try {
      await assignUserApi({
        assign: updatedTaggedIds.map((userId) => ({ userId })),
        code,
        kpiName,
      });
      removeUser(id);
      onAssignmentChange?.();
    } catch (err) {
      console.log("Error removing user:", err);
    }
  };

  const toggleUserList = () => {
    setShowUserList(!showUserList);
    setTriggeredAt(false);
  };

  return (
    <div>
      {/* Capsule and Input Container */}
      <div
        className="input-capsule_container"
        style={{ display: "flex", alignItems: "center" }}
      >
        {/* Display first capsule */}
        {taggedUsers?.length > 0 && (
          <div key={`tagged-${taggedUsers[0]?.id}`} className="user_capsule">
            <div className="user_capsule-details">
              <img
                src={
                  taggedUsers[0].profileImage
                    ? `${constant.IMG_URL}/${taggedUsers[0].profileImage}`
                    : topUser
                }
                height={30}
                width={30}
                alt="User"
                style={{ borderRadius: "50%" }}
              />
              <span>{taggedUsers[0].name}</span>
            </div>
            {isAdmin ? (
              <img
                src={X}
                height={20}
                width={20}
                alt="Remove"
                style={{
                  cursor: "pointer",
                  marginLeft: "5px",
                  borderRadius: "50%",
                }}
                onClick={() => handleDeleteCapsule(taggedUsers[0].id)}
              />
            ) : null}
          </div>
        )}

        {/* Display Avatar Group if there are more than one capsule */}
        {taggedUsers?.length > 1 && (
          <AvatarGroup
            users={taggedUsers.slice(1)}
            maxDisplay={3}
            onClick={toggleUserList}
          />
        )}

        {/* Input Field */}
        <input
          disabled={!isAdmin}
          className="user_tagging-input"
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          placeholder="@Tag Member"
          style={{
            border: "none",
            outline: "none",
            flex: "1",
            minWidth: "150px",
          }}
        />
      </div>

      {/* Suggestions List */}
      {triggeredAt && suggestions?.length > 0 && !showUserList && (
        <ul className="user_tagging-suggestions">
          {suggestions?.map((user) => (
            <li
              className="user-option"
              key={`suggested-${user.id}`}
              onClick={() => handleUserSelect(user)}
              style={{
                cursor: "pointer",
                padding: "5px",
                display: "flex",
                alignItems: "center",
              }}
            >
              {isAdmin ? (
                <img
                  src={`${constant.IMG_URL}/${user.profileImage}`}
                  alt={user.name}
                  height={30}
                  width={30}
                  style={{ borderRadius: "50%" }}
                />
              ) : null}
              <span>{user.name}</span>
            </li>
          ))}
        </ul>
      )}

      {/* Full list of tagged users (shown on clicking the AvatarGroup) */}
      {showUserList && (
        <div className="tagged_users-list" style={{ marginTop: "10px" }}>
          {taggedUsers?.map((user) => (
            <div
              key={`list-${user.id}`}
              className="user_capsule"
              style={{
                display: "flex",
                alignItems: "center",
                padding: "5px 10px",
                marginBottom: "5px",
              }}
            >
              <img
                src={`${constant.IMG_URL}/${user.profileImage}`}
                height={30}
                width={30}
                alt="User"
                style={{ borderRadius: "50%" }}
              />
              <span>{user.name}</span>
              {isAdmin ? (
                <img
                  src={X}
                  height={15}
                  width={15}
                  alt="Remove"
                  style={{ cursor: "pointer", marginLeft: "5px" }}
                  onClick={() => handleDeleteCapsule(user.id)}
                />
              ) : null}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default UserTagging;
